import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Whisper, Tooltip, toaster, Message, Divider, SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import RunningClubMediaDrawer from './running-club-media-drawer';
import { RunningClubMedia, RunningClubMediaPaginationFilterRequest } from '@/models/running-club-media.model';
import { deleteRunningClubMedia, getPagedRunningClubMedias } from '@/services/running-club-media.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { Icon } from '@rsuite/icons';
import { MdDelete } from 'react-icons/md';
import ImageCell from '@/components/ImageCell';
import { ExtensionTypeList } from '@/models/storage.model';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubMediaListProps {
  runningClubId?: number;
}

const RunningClubMediaList : React.FC<RunningClubMediaListProps> = ({runningClubId}) => {
  const [filterReq, setFilterReq] = useState<RunningClubMediaPaginationFilterRequest>(new RunningClubMediaPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);
  
  const fetchData = async (req: RunningClubMediaPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
      };        
      const result: PagedResponseModel<RunningClubMedia> = await getPagedRunningClubMedias(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Club Medias:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };
  
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    fetchData(filterReq);
  };

  const onDeleteItem = (id: number, title?: string) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteRunningClubMedia(id);
        if (result.success) {
          toastSuccessMessage(`Running Club Media Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Running Club Media:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{marginBottom:10}}>Are you sure you want to remove <strong>{title ?? 'Media'}</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };
  
  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={handleOpenDrawer}>
          Add Media
        </Button>

        <Stack spacing={6}>
          {!runningClubId && <FetchingSelect
            label="Running Club"
            fetchData={(text, page) => getRunningClubsLookup(text, page)}
            value={filterReq.runningClubId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runningClubId: dataKey}))}
          />}            
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={80}
      >
        <Column flexGrow={1} align="center" >
          <HeaderCell>Media</HeaderCell>
          <ImageCell dataKey="link" />
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle" sortable>
          <HeaderCell>Media File Extension</HeaderCell>
          <Cell dataKey="fileExtension">
            {(rowData: RunningClubMedia) => ExtensionTypeList.find(r => r.value === rowData.extensionType)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {(rowData: RunningClubMedia) => (              
              <Stack spacing={16}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Media</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={()=> onDeleteItem(rowData.id )} 
                    icon={<Icon as={MdDelete}/>} />
                </Whisper>        
              </Stack>  
              )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <RunningClubMediaDrawer 
        runningClubId={runningClubId}
        open={openDrawer} 
        onClose={handleCloseDrawer} 
      />
    </PageContent>
  );
};

export default RunningClubMediaList;
